import {
  CalendarOutlined,
  CarOutlined,
  ClockCircleOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { Alert, Button, Card, Col, Divider, Row, Tooltip } from 'antd';
import Title from 'antd/lib/typography/Title';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import { FC, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  buildAddress,
  buildTimestampDate,
  buildTimestampTime,
  extractTranslation,
  formatPortalFacilityPath,
} from '../../helpers/formatters';
import { extractPrimaryAddress, getAppointmentTypeName } from '../../helpers/utils';
import { ScreenSizeContext } from '../../pages/_app';
import {
  Patientportal_AppointmentProxyMsg,
  Resource_AppointmentStatus,
} from '../../src/generated/graphql';
import styles from './appointment-details.module.scss';
import { QrCodeModal } from './qr-code-modal';

interface AppointmentHeaderProps {
  appointmentProxy: Maybe<Patientportal_AppointmentProxyMsg> | undefined;
  title?: string;
}

export const AppointmentHeader: FC<AppointmentHeaderProps> = ({ appointmentProxy, title }) => {
  const { formatMessage } = useIntl();

  const { isMobile } = useContext(ScreenSizeContext);

  const [mobile, setMobile] = useState<boolean>(false);
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState<boolean>(false);

  const router = useRouter();

  useEffect(() => {
    setMobile(isMobile);
  }, []);

  const appointment = appointmentProxy?.appointment;
  const appointmentStatus = appointment?.appointmentStatus;
  const appointmentId = appointment?.id;

  const portalFacilities = appointmentProxy?.portalFacilityPath?.portalFacilities;

  const appointmentType = extractTranslation(
    getAppointmentTypeName(appointment?.appointmentType),
    router.locale,
  );

  // Removed as part of: CLN-18325
  // const iCalEvent = appointmentToICalEvent(appointment, portalFacilities, appointmentType);
  //
  // const createGoogleEvent = useGoogleLogin({
  //   onSuccess: (tokenResponse) => {
  //     if (appointment && !!portalFacilities?.length) {
  //       const event = appointmentToGoogleEvent(appointment, portalFacilities, appointmentType);
  //       const requestOptions = {
  //         method: 'POST',
  //         headers: { 'Content-Type': 'application/json' },
  //         body: JSON.stringify({
  //           access_token: tokenResponse.access_token,
  //           event,
  //         }),
  //       };
  //       fetch('/api/google-calendar', requestOptions)
  //         .then((response) => response.json())
  //         .then((data) => {
  //           if (data.status === 200) {
  //             notification.success({
  //               message: formatMessage({ id: 'appointment.add-event.success' }),
  //               description: (
  //                 <Link href={data.data.htmlLink || undefined} target="_blank">
  //                   {formatMessage({ id: 'appointment.add-event.link' })}
  //                 </Link>
  //               ),
  //               duration: 10,
  //             });
  //           } else {
  //             notification.error({
  //               message: formatMessage({ id: 'appointment.add-event.fail' }),
  //             });
  //           }
  //         });
  //     } else {
  //       notification.error({
  //         message: formatMessage({ id: 'appointment.add-event.fail' }),
  //       });
  //     }
  //   },
  //   scope: 'openid email profile https://www.googleapis.com/auth/calendar',
  //   flow: 'implicit',
  // });

  const booked = appointmentStatus === Resource_AppointmentStatus.AppointmentStatusBooked;

  const officeHour = portalFacilities?.[portalFacilities?.length - 1];

  const unverified = appointment?.unverified;

  const address = extractPrimaryAddress(officeHour?.contact);

  return (
    <Card>
      <Title>
        {title ||
          formatMessage(
            { id: 'appointment-details' },
            {
              appointmentType: appointmentType || '-',
            },
          )}
      </Title>
      <Row>
        <Col span={24}>
          <div className={styles.calendarDiv}>
            <CalendarOutlined />
          </div>
          <div className={styles.portalFacilityPath}>
            <Row>
              <Col span={24}>{formatPortalFacilityPath(portalFacilities, true)}</Col>
            </Row>
            <Row className={styles.appointmentDate}>
              <Col span={24}>
                <span>
                  <CalendarOutlined /> {buildTimestampDate(appointment?.startTime)}
                </span>
                <span className={styles.appointmentTime} style={{ marginLeft: 20 }}>
                  <ClockCircleOutlined /> {buildTimestampTime(appointment?.startTime)}
                </span>
              </Col>
            </Row>
          </div>
        </Col>
        <Col span={24}>
          {booked && (
            <div>
              <Divider style={{ marginBottom: 22 }} />
              <Row justify="space-evenly">
                {/* Removed as part of CLN-18325 */}
                {/*<Col*/}
                {/*  span={6}*/}
                {/*  style={{*/}
                {/*    textAlign: "center",*/}
                {/*    display: "flex",*/}
                {/*    alignItems: "center",*/}
                {/*    justifyContent: "center",*/}
                {/*  }}*/}
                {/*>*/}
                {/*  <Tooltip*/}
                {/*    title={formatMessage({*/}
                {/*      id: "appointment-successful.add-google-calendar.short",*/}
                {/*    })}*/}
                {/*  >*/}
                {/*    <Button*/}
                {/*      size={"large"}*/}
                {/*      shape={mobile ? "circle" : "round"}*/}
                {/*      key="addEvent"*/}
                {/*      type="ghost"*/}
                {/*      onClick={() => createGoogleEvent()}*/}
                {/*      icon={<GoogleOutlined />}*/}
                {/*    >*/}
                {/*      {!mobile &&*/}
                {/*        formatMessage({*/}
                {/*          id: "appointment.google-calendar.short",*/}
                {/*        })}*/}
                {/*    </Button>*/}
                {/*  </Tooltip>*/}
                {/*</Col>*/}
                {/* Removed as part of CLN-18325 */}
                {/*{mobile && (*/}
                {/*  <Col*/}
                {/*    span={8}*/}
                {/*    style={{*/}
                {/*      textAlign: 'center',*/}
                {/*      display: 'flex',*/}
                {/*      alignItems: 'center',*/}
                {/*      justifyContent: 'center',*/}
                {/*    }}*/}
                {/*  >*/}
                {/*    <Tooltip*/}
                {/*      title={formatMessage({*/}
                {/*        id: 'appointment-successful.add-ical-calendar.short',*/}
                {/*      })}*/}
                {/*    >*/}
                {/*      /!* @ts-ignore *!/*/}
                {/*      <ICalendarLink event={iCalEvent}>*/}
                {/*        <Button*/}
                {/*          size={'large'}*/}
                {/*          shape={mobile ? 'circle' : 'round'}*/}
                {/*          key="addEventIcal"*/}
                {/*          type="ghost"*/}
                {/*          icon={<CalendarOutlined />}*/}
                {/*        >*/}
                {/*          {!mobile &&*/}
                {/*            formatMessage({*/}
                {/*              id: 'appointment.calendar.short',*/}
                {/*            })}*/}
                {/*        </Button>*/}
                {/*      </ICalendarLink>*/}
                {/*    </Tooltip>*/}
                {/*  </Col>*/}
                {/*)}*/}

                <Col
                  span={8}
                  style={{
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Tooltip
                    title={formatMessage({
                      id: 'appointment.qr-code',
                    })}
                  >
                    <Button
                      size={'large'}
                      shape={mobile ? 'circle' : 'round'}
                      key="qr-code"
                      type="ghost"
                      icon={<QrcodeOutlined />}
                      onClick={() => setQrCodeModalOpen(true)}
                    >
                      {!mobile &&
                        formatMessage({
                          id: 'appointment.qr-code.short',
                        })}
                    </Button>
                  </Tooltip>
                </Col>
                {address && (
                  <Col
                    span={8}
                    style={{
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Tooltip
                      title={formatMessage({
                        id: 'appointment.route',
                      })}
                    >
                      <Button
                        size={'large'}
                        shape={mobile ? 'circle' : 'round'}
                        icon={<CarOutlined />}
                        type={'primary'}
                        onClick={() => {
                          window.open(
                            `https://maps.google.com/?q=${buildAddress(address)}`,
                            '_blank',
                          );
                        }}
                      >
                        {!mobile &&
                          formatMessage({
                            id: 'appointment.route.short',
                          })}
                      </Button>
                    </Tooltip>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </Col>
      </Row>
      {appointmentId && (
        <QrCodeModal
          open={qrCodeModalOpen}
          onClose={() => setQrCodeModalOpen(false)}
          id={appointmentId}
        />
      )}
      {unverified && (
        <Alert
          style={{
            marginTop: 22,
            backgroundColor: 'rgba(160,68,68,0.3)',
            color: 'white',
          }}
          type={'error'}
          message={formatMessage({
            id: 'appointment.not-verified.description',
          })}
        />
      )}
    </Card>
  );
};
